import { memo, useEffect, useState, useCallback, useRef } from 'react';
import { motion, useSpring, useMotionValue } from 'framer-motion';
import { useReduceMotion } from '@/hooks/useReduceMotion';
import { useIsMobile } from '@/hooks/use-mobile';

interface GlowCursorProps {
  enabled?: boolean;
  size?: number;
  trailLength?: number;
  className?: string;
}

interface TrailPoint {
  x: number;
  y: number;
  id: string;
}

/**
 * Glow Cursor Effect
 * A futuristic glowing cursor trail that follows mouse movement.
 * Creates an immersive, premium interactive experience.
 * Automatically disabled on mobile devices.
 */
export const GlowCursor = memo(({ 
  enabled = true, 
  size = 24,
  trailLength = 8,
  className = '' 
}: GlowCursorProps) => {
  const reduceMotion = useReduceMotion();
  const isMobile = useIsMobile();
  
  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);
  
  const springConfig = { damping: 25, stiffness: 300 };
  const cursorXSpring = useSpring(cursorX, springConfig);
  const cursorYSpring = useSpring(cursorY, springConfig);

  // Unique ID counter for trail points
  const idCounterRef = useRef(0);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    cursorX.set(e.clientX);
    cursorY.set(e.clientY);
    setIsVisible(true);
    
    if (!reduceMotion) {
      idCounterRef.current += 1;
      const uniqueId = `trail-${idCounterRef.current}`;
      setTrail(prev => {
        const newTrail = [
          { x: e.clientX, y: e.clientY, id: uniqueId },
          ...prev.slice(0, trailLength - 1)
        ];
        return newTrail;
      });
    }
  }, [cursorX, cursorY, reduceMotion, trailLength]);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
    setTrail([]);
  }, []);

  useEffect(() => {
    if (!enabled || isMobile || reduceMotion) return;

    window.addEventListener('mousemove', handleMouseMove);
    document.body.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, isMobile, reduceMotion, handleMouseMove, handleMouseLeave]);

  // Clear stale trail points
  useEffect(() => {
    if (reduceMotion || !enabled) return;
    
    const interval = setInterval(() => {
      setTrail(prev => prev.slice(0, trailLength));
    }, 50);

    return () => clearInterval(interval);
  }, [reduceMotion, enabled, trailLength]);

  if (!enabled || isMobile || reduceMotion) return null;

  return (
    <div className={`fixed inset-0 pointer-events-none z-[9999] ${className}`}>
      {/* Trail particles */}
      {trail.map((point, index) => {
        const opacity = 1 - (index / trailLength);
        const scale = 1 - (index / trailLength) * 0.6;
        
        return (
          <motion.div
            key={point.id}
            className="absolute rounded-full"
            style={{
              width: size * scale,
              height: size * scale,
              left: point.x,
              top: point.y,
              x: '-50%',
              y: '-50%',
              background: `radial-gradient(circle, hsl(var(--primary) / ${0.4 * opacity}) 0%, transparent 70%)`,
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: opacity * 0.6, scale }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.15 }}
          />
        );
      })}

      {/* Main cursor glow */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: size,
          height: size,
          x: cursorXSpring,
          y: cursorYSpring,
          translateX: '-50%',
          translateY: '-50%',
        }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ 
          opacity: isVisible ? 0.8 : 0, 
          scale: isVisible ? 1 : 0 
        }}
        transition={{ duration: 0.2 }}
      >
        {/* Inner glow */}
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.6) 0%, hsl(var(--primary) / 0.2) 50%, transparent 70%)',
          }}
        />
        
        {/* Outer soft glow */}
        <div 
          className="absolute -inset-2 rounded-full blur-md"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 60%)',
          }}
        />
      </motion.div>
    </div>
  );
});

GlowCursor.displayName = 'GlowCursor';
